import { Controller } from "@hotwired/stimulus"

const VALID_US_STATES = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "AS", "GU", "MP", "PR", "VI"
])

export default class extends Controller {
  static targets = [
    "input",
    "street",
    "city",
    "state",
    "zip",
    "suggestions"
  ]

  static values = {
    maxResults: { type: Number, default: 5 }
  }

  connect() {
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)
    this.currentSuggestions = []
    this.selectedIndex = -1
    this.addressSelected = false
    this.loadSavedAddress()
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }
  }

  loadSavedAddress() {
    const savedStreet = this.hasStreetTarget ? this.streetTarget.value : ''
    const savedCity = this.hasCityTarget ? this.cityTarget.value : ''
    const savedState = this.hasStateTarget ? this.stateTarget.value : ''
    const savedZip = this.hasZipTarget ? this.zipTarget.value : ''

    if (savedStreet && savedStreet.trim()) {
      const parts = [savedCity, savedState, savedZip].filter(Boolean)
      const cityStateZip = parts.join(', ')
      const displayAddress = cityStateZip ? `${savedStreet}, ${cityStateZip}` : savedStreet

      if (this.hasInputTarget) {
        this.inputTarget.value = displayAddress
        this.addressSelected = true
      }
    }
  }

  handleInput(event) {
    const value = event.target.value
    this.addressSelected = false

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    if (!value || value.trim().length < 3) {
      this.hideSuggestions()
      if (!value || !value.trim()) this.clearHiddenFields()
      return
    }

    this.debounceTimeout = setTimeout(() => {
      this.fetchSuggestions(value)
    }, 250)
  }

  handleBlur() {
    setTimeout(() => {
      if (!this.addressSelected && this.hasInputTarget && this.inputTarget.value.trim()) {
        this.parseManualAddress(this.inputTarget.value.trim())
      }
    }, 200)
  }

  parseManualAddress(addressText) {
    const parsed = this.parseAddressComponents(addressText)

    if (this.hasStreetTarget && parsed.street) {
      this.streetTarget.value = parsed.street
      this.triggerInputEvent(this.streetTarget)
    }

    if (this.hasCityTarget && parsed.city) {
      this.cityTarget.value = parsed.city
      this.triggerInputEvent(this.cityTarget)
    }

    if (this.hasStateTarget && parsed.state) {
      if (VALID_US_STATES.has(parsed.state)) {
        this.stateTarget.value = parsed.state
        this.triggerInputEvent(this.stateTarget)
      } else {
        this.stateTarget.value = ''
        this.triggerInputEvent(this.stateTarget)
      }
    }

    if (this.hasZipTarget && parsed.zip) {
      this.zipTarget.value = parsed.zip
      this.triggerInputEvent(this.zipTarget)
    }

    this.addressSelected = true

    this.element.dispatchEvent(new CustomEvent('address-autocomplete:manual', {
      bubbles: true,
      detail: { address: parsed, raw: addressText }
    }))
  }

  parseAddressComponents(text) {
    const result = { street: '', city: '', state: '', zip: '' }

    text = text.replace(/[,\s]+$/, '').trim()

    const zipMatch = text.match(/,?\s*(\d{5})(?:-\d{4})?$/)
    if (zipMatch) {
      result.zip = zipMatch[1]
      text = text.replace(zipMatch[0], '').trim()
    }

    text = text.replace(/[,\s]+$/, '')

    const stateMatch = text.match(/,?\s*([A-Z]{2})$/i)
    if (stateMatch) {
      result.state = stateMatch[1].toUpperCase()
      text = text.replace(stateMatch[0], '').trim()
    }

    text = text.replace(/[,\s]+$/, '')

    const parts = text.split(',').map(p => p.trim()).filter(Boolean)

    if (parts.length >= 2) {
      result.street = parts[0]
      result.city = parts[parts.length - 1]
    } else if (parts.length === 1) {
      const streetCityMatch = parts[0].match(/^(.+?\s(?:st|street|ave|avenue|blvd|boulevard|dr|drive|rd|road|ln|lane|way|ct|court|pl|place|cir|circle|pkwy|parkway|hwy|highway)\.?)\s+(.+)$/i)
      if (streetCityMatch) {
        result.street = streetCityMatch[1].trim()
        result.city = streetCityMatch[2].trim()
      } else {
        result.street = parts[0]
      }
    }

    return result
  }

  async fetchSuggestions(searchTerm, selected = null) {
    try {
      let url = `/api/address/autocomplete?query=${encodeURIComponent(searchTerm)}`

      if (selected) {
        url += `&selected=${encodeURIComponent(selected)}`
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.suggestions && data.suggestions.length > 0) {
        this.displaySuggestions(data.suggestions, selected)
      } else {
        this.hideSuggestions()
      }
    } catch (error) {
      console.error('[smartystreets] Error fetching suggestions:', error)
      this.hideSuggestions()
    }
  }

  displaySuggestions(suggestions, isSecondaryLookup = false) {
    if (!this.hasSuggestionsTarget) return

    const suggestionsHTML = suggestions.map((suggestion, index) => {
      const hasMultipleEntries = suggestion.entries > 1
      const streetLine = this.formatStreetLine(suggestion)
      const cityLine = this.formatCityLine(suggestion)

      let entryIndicator = ''
      if (hasMultipleEntries && !isSecondaryLookup) {
        entryIndicator = `<span class="text-xs text-blue-600 ml-2">(${suggestion.entries} units)</span>`
      }

      return `
        <div class="autocomplete-suggestion px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${index === 0 ? 'bg-gray-50' : ''}"
             data-index="${index}"
             data-has-entries="${hasMultipleEntries}"
             data-action="mousedown->smartystreets-autocomplete#selectSuggestion">
          <div class="font-medium text-gray-900">${streetLine}${entryIndicator}</div>
          <div class="text-sm text-gray-600">${cityLine}</div>
        </div>
      `
    }).join('')

    this.suggestionsTarget.innerHTML = suggestionsHTML
    this.suggestionsTarget.classList.remove('hidden')

    this.currentSuggestions = suggestions
    this.selectedIndex = -1
    this.isSecondaryLookup = isSecondaryLookup
  }

  formatStreetLine(suggestion) {
    let line = suggestion.street_line || ''
    if (suggestion.secondary) {
      line += ` ${suggestion.secondary}`
    }
    return line
  }

  formatCityLine(suggestion) {
    const parts = []
    if (suggestion.city) parts.push(suggestion.city)
    if (suggestion.state) parts.push(suggestion.state)
    if (suggestion.zipcode) parts.push(suggestion.zipcode)
    return parts.join(', ')
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add('hidden')
      this.suggestionsTarget.innerHTML = ''
    }
    this.currentSuggestions = []
    this.selectedIndex = -1
    this.isSecondaryLookup = false
  }

  selectSuggestion(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = parseInt(event.currentTarget.dataset.index)
    const suggestion = this.currentSuggestions[index]

    if (suggestion) {
      if (suggestion.entries > 1 && !this.isSecondaryLookup) {
        this.handleMultiEntryAddress(suggestion)
      } else {
        this.applySuggestion(suggestion)
      }
    }
  }

  handleMultiEntryAddress(suggestion) {
    const selectedValue = this.buildSelectedValue(suggestion)

    const baseAddress = suggestion.street_line + (suggestion.secondary ? ` ${suggestion.secondary}` : '')
    this.inputTarget.value = baseAddress + ' '
    this.inputTarget.focus()

    this.fetchSuggestions(this.inputTarget.value, selectedValue)
  }

  buildSelectedValue(suggestion) {
    let value = suggestion.street_line
    if (suggestion.secondary) {
      value += ` ${suggestion.secondary}`
    }
    value += ` (${suggestion.entries}) ${suggestion.city} ${suggestion.state} ${suggestion.zipcode}`
    return value
  }

  applySuggestion(suggestion) {
    const streetLine = suggestion.street_line + (suggestion.secondary ? ` ${suggestion.secondary}` : '')
    const cityStateZip = this.formatCityLine(suggestion)
    this.inputTarget.value = `${streetLine}, ${cityStateZip}`

    if (this.hasStreetTarget) {
      this.streetTarget.value = streetLine
      this.triggerInputEvent(this.streetTarget)
    }

    if (this.hasCityTarget) {
      this.cityTarget.value = suggestion.city || ''
      this.triggerInputEvent(this.cityTarget)
    }

    if (this.hasStateTarget) {
      this.stateTarget.value = suggestion.state || ''
      this.triggerInputEvent(this.stateTarget)
    }

    if (this.hasZipTarget) {
      this.zipTarget.value = suggestion.zipcode || ''
      this.triggerInputEvent(this.zipTarget)
    }

    this.hideSuggestions()
    this.addressSelected = true

    this.element.dispatchEvent(new CustomEvent('address-autocomplete:selected', {
      bubbles: true,
      detail: { address: suggestion }
    }))

    this.announceToScreenReader(`Address selected: ${streetLine}, ${cityStateZip}`)
  }

  handleKeydown(event) {
    if (!this.currentSuggestions || this.currentSuggestions.length === 0) {
      return
    }

    const suggestionElements = this.suggestionsTarget.querySelectorAll('.autocomplete-suggestion')

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.currentSuggestions.length - 1)
        this.updateSelectedSuggestion(suggestionElements)
        break

      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.updateSelectedSuggestion(suggestionElements)
        break

      case 'Enter':
        event.preventDefault()
        if (this.selectedIndex >= 0) {
          const suggestion = this.currentSuggestions[this.selectedIndex]
          if (suggestion.entries > 1 && !this.isSecondaryLookup) {
            this.handleMultiEntryAddress(suggestion)
          } else {
            this.applySuggestion(suggestion)
          }
        } else if (this.currentSuggestions.length > 0) {
          const suggestion = this.currentSuggestions[0]
          if (suggestion.entries > 1 && !this.isSecondaryLookup) {
            this.handleMultiEntryAddress(suggestion)
          } else {
            this.applySuggestion(suggestion)
          }
        }
        break

      case 'Escape':
        this.hideSuggestions()
        break

      case 'Tab':
        if (this.currentSuggestions.length > 0 && this.selectedIndex === -1) {
          this.applySuggestion(this.currentSuggestions[0])
        }
        this.hideSuggestions()
        break
    }
  }

  updateSelectedSuggestion(suggestionElements) {
    suggestionElements.forEach((el, index) => {
      if (index === this.selectedIndex) {
        el.classList.add('bg-blue-50')
        el.classList.remove('bg-gray-50')
        el.scrollIntoView({ block: 'nearest' })
      } else {
        el.classList.remove('bg-blue-50')
        if (index === 0 && this.selectedIndex === -1) {
          el.classList.add('bg-gray-50')
        } else {
          el.classList.remove('bg-gray-50')
        }
      }
    })
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }

  clearHiddenFields() {
    const targets = ["street", "city", "state", "zip"]
    targets.forEach(name => {
      const target = `has${name.charAt(0).toUpperCase() + name.slice(1)}Target`
      if (this[target]) {
        this[`${name}Target`].value = ""
        this.triggerInputEvent(this[`${name}Target`])
      }
    })
  }

  triggerInputEvent(field) {
    field.dispatchEvent(new Event('input', { bubbles: true }))
    field.dispatchEvent(new Event('change', { bubbles: true }))
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', 'polite')
    announcement.className = 'sr-only'
    announcement.textContent = message

    document.body.appendChild(announcement)

    setTimeout(() => {
      if (announcement.parentNode) {
        document.body.removeChild(announcement)
      }
    }, 1000)
  }
}
